/* eslint camelcase: ["off"] */
import React, { Component } from 'react';
import CategoryDropdown from './CategoryDropdown';
import Checkbox from '../../elements/Checkbox';
import Input from '../../elements/Input';
import ModalBody from '../modal/ModalBody';
import ModalControls from '../modal/ModalControls';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';
import type { OvertimePeriodName, Project, ProjectCategory } from '../../../types';

const OVERTIME_PERIODS: Array<{ period_name: OvertimePeriodName, label: string }> = [
  { period_name: 'weekday_before_hours', label: 'Weekday before hours' },
  { period_name: 'weekday_regular', label: 'Weekday regular' },
  { period_name: 'weekday_after_hours', label: 'Weekday after hours' },
  { period_name: 'weekday_night_shift', label: 'Weekday night shift' },
  { period_name: 'weekend_holiday_day_shift', label: 'Weekend/holiday day shift' },
  { period_name: 'weekend_holiday_night_shift', label: 'Weekend/holiday night shift' },
];

const EMPTY_FORM = {
  title: '',
  client_name: '',
  active: true,
  billable: false,
  categorize_logs: false,
  show_task_id: false,
  task_id_required: false,
  monthly_limit: null,
  weekly_report_enabled: false,
  allowed_category_ids: [],
  overtime_rate_rules: OVERTIME_PERIODS.map(({ period_name }) => ({
    period_name,
    multiplier: '',
  })),
};

type State = {
  form: {
    title: string,
    client_name: string,
    active: boolean,
    billable: boolean,
    categorize_logs: boolean,
    show_task_id: boolean,
    task_id_required: boolean,
    monthly_limit: number | null,
    weekly_report_enabled: boolean,
    allowed_category_ids: Array<number>,
    overtime_rate_rules: Array<{
      period_name: OvertimePeriodName,
      multiplier: number | string
    }>
  }
}

type Props = {
  onProjectSubmit: (form: any) => void,
  onProjectDelete: (projectId: number) => void,
  onCloseClick: () => void,
  saveButtonText: string,
  title: string,
  edit: boolean,
  disableButtons?: boolean,
  project: Project | null | void,
  allActiveCategories: Array<ProjectCategory>,
}

class ProjectsModal extends Component<Props, State> {
  static defaultProps = {
    disableButtons: false,
  };

  state = {
    form: EMPTY_FORM,
  };

  componentDidUpdate = (prevProps: Props) => {
    const { edit, project, title } = this.props;

    if (prevProps.edit !== edit || prevProps.title !== title) {
      if (edit && project) {
        const {
          title: projectTitle, client_name, active, billable,
          categorize_logs, monthly_limit, weekly_report_enabled,
          show_task_id, task_id_required,
        } = project;
        this.setState({
          form: {
            title: projectTitle,
            client_name,
            active,
            billable,
            categorize_logs,
            monthly_limit,
            weekly_report_enabled,
            show_task_id,
            task_id_required,
            allowed_category_ids: project.allowed_category_ids || [],
            overtime_rate_rules: OVERTIME_PERIODS.map(({ period_name }) => ({
              period_name,
              multiplier: project.overtime_rate_rules
                ?.find((rule) => rule.period_name === period_name)?.multiplier || '',
            })),
          },
        });
      } else {
        this.setState({
          form: EMPTY_FORM,
        });
      }
    }
  };

  changeFormField = (field: string, value: any) => {
    const { form } = this.state;
    this.setState({
      form: {
        ...form,
        [field]: value,
      },
    });
  };

  handleCategoryChange = (event: any) => {
    const categoryId = parseInt(event.target.value, 10);
    const { form } = this.state;

    let newAllowedCategories = [...form.allowed_category_ids];

    if (event.target.checked) {
      newAllowedCategories.push(categoryId);
    } else {
      newAllowedCategories = newAllowedCategories.filter(id => id !== categoryId);
    }
    this.changeFormField('allowed_category_ids', newAllowedCategories);
  };

  handleProjectTitleChange = (event: any) => {
    this.changeFormField('title', event.target.value);
  };

  handleProjectClientNameChange = (event: any) => {
    this.changeFormField('client_name', event.target.value);
  };

  handleProjectActiveChange = (event: any) => {
    this.changeFormField('active', event.target.checked);
  };

  handleProjectBillableChange = (event: any) => {
    this.changeFormField('billable', event.target.checked);
  };

  handleProjectCategorizeLogsChange = (event: any) => {
    this.changeFormField('categorize_logs', event.target.checked);
  };

  handleProjectShowTaskIdChange = (event: any) => {
    this.changeFormField('show_task_id', event.target.checked);
  };

  handleProjectTaskIdRequiredChange = (event: any) => {
    this.changeFormField('task_id_required', event.target.checked);
  };

  handleProjectMonthlyLimitChange = (event: any) => {
    const value = event.target.value > 0 ? event.target.value : (event.target.value * -1);
    this.changeFormField('monthly_limit', value);
  };

  handleProjectWeeklyReportEnabledChange = (event: any) => {
    this.changeFormField('weekly_report_enabled', event.target.checked);
  };

  handleOvertimeRateRuleChange = (periodName: OvertimePeriodName, event: any) => {
    const { form } = this.state;
    const multiplier = event.target.value;

    this.changeFormField(
      'overtime_rate_rules',
      form.overtime_rate_rules.map((rule) => (
        rule.period_name === periodName ? { ...rule, multiplier } : rule
      )),
    );
  };

  isFormValid = () => {
    const { form: { title, client_name } } = this.state;

    return title !== '' && client_name !== '';
  };

  handleSubmit = () => {
    const { onProjectSubmit, edit, project } = this.props;
    const { form } = this.state;
    const overtime_rate_rules = form.overtime_rate_rules.map(rule => ({
      ...rule,
      multiplier: Number(rule.multiplier),
    }));
    if (edit && project) {
      const formData = { ...form, overtime_rate_rules, id: project.id };
      onProjectSubmit(formData);
    } else {
      onProjectSubmit({ ...form, overtime_rate_rules });
    }
  };

  handleDeleteClick = () => {
    const { project, onProjectDelete } = this.props;
    if (project) {
      onProjectDelete(project.id);
    }
  };

  render(): React.ReactNode {
    const {
      saveButtonText, title, edit, project, disableButtons, onCloseClick,
    } = this.props;
    const { form } = this.state;

    return (
      <>
        <ModalHeader
          title={title}
          onCloseClick={onCloseClick}
        />
        <ModalBody>
          <form>
            <div className="form-group">
              <Input
                id="add-title"
                label="Project title"
                name="title"
                value={form.title}
                onChange={this.handleProjectTitleChange}
              />
            </div>
            <div className="form-group">
              <Input
                id="add-client-name"
                label="Client name"
                name="client-name"
                value={form.client_name}
                onChange={this.handleProjectClientNameChange}
              />
            </div>
            <div className="form-group">
              <Input
                type="number"
                id="add-monthly-limit"
                label="Monthly Limit"
                name="monthly-limit"
                value={form.monthly_limit || ''}
                onChange={this.handleProjectMonthlyLimitChange}
              />
            </div>
            {form.categorize_logs && (
              <div className="form-group relative">
                <CategoryDropdown
                  categories={this.props.allActiveCategories}
                  selectedIds={form.allowed_category_ids}
                  onChange={this.handleCategoryChange}
                />
              </div>
            )}
            <div className="form-check">
              <Checkbox
                name="active"
                label="Active"
                id="add-active"
                checked={form.active}
                onChange={this.handleProjectActiveChange}
              />
            </div>
            <div className="form-check">
              <Checkbox
                name="billable"
                label="Billable"
                id="add-billable"
                checked={form.billable}
                onChange={this.handleProjectBillableChange}
              />
            </div>
            <div className="form-check">
              <Checkbox
                name="categorize_logs"
                label="Categorize Logs"
                id="add-categorize-logs"
                checked={form.categorize_logs}
                onChange={this.handleProjectCategorizeLogsChange}
              />
            </div>
            <div className="form-check">
              <Checkbox
                name="show_task_id"
                label="Show task ID in reports"
                id="add-show-task-id"
                checked={form.show_task_id}
                onChange={this.handleProjectShowTaskIdChange}
              />
            </div>
            <div className="form-check">
              <Checkbox
                name="task_id_required"
                label="Task ID required"
                id="add-task-id-required"
                checked={form.task_id_required}
                onChange={this.handleProjectTaskIdRequiredChange}
              />
            </div>
            <div className="form-check">
              <Checkbox
                name="weekly_report_enabled"
                label="Weekly Report Enabled"
                id="add-weekly_report_enabled"
                checked={form.weekly_report_enabled}
                onChange={this.handleProjectWeeklyReportEnabledChange}
              />
            </div>
            <>
              <hr />
              <h4>Overtime rate rules</h4>
              {OVERTIME_PERIODS.map(({ period_name, label }) => (
                <div className="form-group" key={period_name}>
                  <Input
                    type="number"
                    id={`overtime-rate-rule-${period_name}`}
                    label={label}
                    name={period_name}
                    value={form.overtime_rate_rules
                      .find((rule) => rule.period_name === period_name)?.multiplier || ''}
                    onChange={(event: any) => this.handleOvertimeRateRuleChange(period_name, event)}
                    step="0.01"
                    min="0.01"
                  />
                </div>
              ))}
            </>
          </form>
        </ModalBody>
        <ModalFooter>
          <ModalControls
            showEditButton={edit && !!project}
            disableButtons={disableButtons || false}
            isFormValid={!this.isFormValid()}
            onSubmitButtonClick={this.handleSubmit}
            onDeleteButtonClick={this.handleDeleteClick}
            onCloseButtonClick={onCloseClick}
            submitButtonText={saveButtonText}
            deleteButtonText="Delete project"
          />
        </ModalFooter>
      </>
    );
  }
}

export default ProjectsModal;
